import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { receitasAPI, clientesAPI, definicoesAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Loader2, 
  FileText, 
  Edit, 
  Printer, 
  Tag, 
  FileCheck,
  User,
  Package,
  Calendar,
  MessageSquare
} from 'lucide-react';
import Swal from 'sweetalert2';
import { generateReceitaPDF, generateEtiquetaPDF, generateSingleEtiquetaPDF, generateAtestadoPDF, printPDF } from '../utils/pdfGenerator';

export const ReceitaView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receita, setReceita] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [definicoes, setDefinicoes] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle('Detalhes da Receita');
  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [receitaRes, definicoesRes] = await Promise.all([
        receitasAPI.getOne(id),
        definicoesAPI.get()
      ]);
      
      setReceita(receitaRes.data);
      setDefinicoes(definicoesRes.data);

      // Fetch cliente details
      try {
        const clienteRes = await clientesAPI.getOne(receitaRes.data.cliente_id);
        setCliente(clienteRes.data);
      } catch (e) {
        console.log('Cliente not found');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Receita não encontrada.',
        confirmButtonColor: '#0D9488'
      });
      navigate('/receitas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrintReceita = async () => {
    const doc = await generateReceitaPDF(receita, cliente, definicoes);
    printPDF(doc);
  };

  const handlePrintEtiqueta = () => {
    const doc = generateEtiquetaPDF(receita, cliente, definicoes);
    printPDF(doc);
  };

  const handlePrintEtiquetaProduto = (produto) => {
    const doc = generateSingleEtiquetaPDF(produto, cliente, receita, definicoes);
    printPDF(doc);
  };

  const handlePrintAtestado = async () => {
    const doc = await generateAtestadoPDF(receita, cliente, definicoes);
    printPDF(doc);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!receita) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="receita-view-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/receitas">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              Receita {receita.numero_receita}
            </h1>
            <p className="text-slate-500 mt-1">
              Criada em {formatDate(receita.created_at)}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handlePrintReceita}
            data-testid="print-receita-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Receita
          </Button>
          <Button
            variant="outline"
            onClick={handlePrintEtiqueta}
            data-testid="print-etiqueta-btn"
          >
            <Tag className="h-4 w-4 mr-2" />
            Todas Etiquetas
          </Button>
          <Button
            variant="outline"
            onClick={handlePrintAtestado}
            data-testid="print-atestado-btn"
          >
            <FileCheck className="h-4 w-4 mr-2" />
            Atestado
          </Button>
          <Link to={`/receitas/${id}/editar`}>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-receita-btn">
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Produtos */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Package className="h-5 w-5 text-amber-600" />
                Produtos / Medicamentos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {receita.produtos.map((produto, index) => (
                  <div 
                    key={index} 
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{produto.produto_nome}</h4>
                        <p className="text-sm text-slate-500 mt-1">
                          Quantidade: <span className="font-medium text-slate-700">{produto.quantidade}</span>
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrintEtiquetaProduto(produto)}
                        data-testid={`print-etiqueta-produto-${index}`}
                      >
                        <Tag className="h-3.5 w-3.5 mr-1.5" />
                        Etiqueta
                      </Button>
                    </div>
                    {produto.observacao && (
                      <div className="mt-3 p-3 bg-white rounded border border-slate-100">
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Observação:</span> {produto.observacao}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Observações Internas */}
          {receita.observacoes_internas && (
            <Card className="border-slate-200 border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                  Observações Internas
                </CardTitle>
                <p className="text-xs text-amber-600">Visível apenas para a equipa</p>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{receita.observacoes_internas}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Cliente Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <User className="h-5 w-5 text-amber-600" />
                Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">Nome</p>
                <p className="font-medium text-slate-900">{cliente?.nome || receita.cliente_nome}</p>
              </div>
              {cliente?.id_especial && (
                <div>
                  <p className="text-sm text-slate-500">ID Especial</p>
                  <p className="font-medium text-amber-600">{cliente.id_especial}</p>
                </div>
              )}
              {cliente?.nif && (
                <div>
                  <p className="text-sm text-slate-500">NIF</p>
                  <p className="font-medium text-slate-900">{cliente.nif}</p>
                </div>
              )}
              {cliente?.telemovel && (
                <div>
                  <p className="text-sm text-slate-500">Telemóvel</p>
                  <p className="font-medium text-slate-900">{cliente.telemovel}</p>
                </div>
              )}
              {cliente?.morada && (
                <div>
                  <p className="text-sm text-slate-500">Morada</p>
                  <p className="text-slate-700">
                    {cliente.morada}
                    {cliente.codigo_postal && <>, {cliente.codigo_postal}</>}
                    {cliente.cidade && <> {cliente.cidade}</>}
                  </p>
                </div>
              )}
              {cliente && (
                <Link to={`/clientes/${cliente.id}/editar`}>
                  <Button variant="outline" size="sm" className="w-full mt-2">
                    Ver Cliente
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>

          {/* Receita Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Informações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">Número</p>
                <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded inline-block">
                  {receita.numero_receita}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Data de Criação</p>
                <p className="font-medium text-slate-900">{formatDate(receita.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total de Produtos</p>
                <p className="font-medium text-slate-900">{receita.produtos.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
